import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useUpdateServer } from '@/hooks/use-update-server';
import { ApiError } from '@/lib/api-client';
import type { ServerDetail } from '@/types/fleet';

/**
 * Protocols an operator may arm on a server.
 *
 * `tb_protocols` (+ the active `tb_server_protocols` join rows) is the source
 * of truth for what the platform actually serves; this list mirrors the subset
 * of the API's `VpsProtocol` enum that survives fleet-DTO validation. WireGuard,
 * OpenVPN and Shadowsocks are deliberately absent — see RETIRED_PROTOCOLS.
 *
 * Not offered here on purpose: `https-connect` and `mixnet`. Both are seeded
 * server-side (proxy-ingress provisioning / the mixnet playbook) and a server
 * that already has them keeps them — an unchecked box would silently strip
 * them on Save.
 */
const PROTOCOLS = ['amneziawg', 'v2ray', 'tor', 'coredns'];

/**
 * Client tunnel protocols the platform has retired. The API rejects these on
 * POST /servers and PATCH /servers/:name with a 400, so they can only be turned
 * OFF here — never re-armed. Re-arming one produced an active
 * `tb_server_protocols` row that made `/vpn/connect` answer 503 "No healthy
 * exit nodes available" for every client pinning it.
 */
const RETIRED_PROTOCOLS = ['wireguard', 'openvpn', 'shadowsocks'];

/** Connection-mode capabilities (VpsCapability enum). */
const CAPABILITIES = ['standard', 'stealth', 'tor', 'mixnet', 'p2p'];

function toggle(set: string[], value: string): string[] {
  return set.includes(value) ? set.filter((v) => v !== value) : [...set, value];
}

/** Order-insensitive set equality. */
function sameSet(a: string[], b: string[]): boolean {
  return a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');
}

/**
 * Connection-mode editor — sets a server's protocols and capabilities.
 *
 * The full selected set is sent on Save; the API replaces the server's child
 * rows (an unchecked mode is removed). `Save` is disabled until something
 * changes from the server's current modes.
 */
export function ConnectionModeEditor({ server }: { server: ServerDetail }): JSX.Element {
  const initialProtocols = server.protocols.map((p) => p.protocol);
  const initialCapabilities = [...server.capabilities];

  const [protocols, setProtocols] = useState<string[]>(initialProtocols);
  const [capabilities, setCapabilities] = useState<string[]>(initialCapabilities);
  const update = useUpdateServer();

  // Retired protocols this server still has armed. Rendered so an operator can
  // clear them; the checkbox locks once unchecked because the API rejects a
  // body that re-adds one.
  const retired = RETIRED_PROTOCOLS.filter((p) => initialProtocols.includes(p));

  const dirty =
    !sameSet(protocols, initialProtocols) || !sameSet(capabilities, initialCapabilities);

  function save(): void {
    update.mutate(
      { name: server.name, patch: { protocols, capabilities } },
      {
        onSuccess: () => toast.success('Connection modes updated.'),
        onError: (err) =>
          toast.error(err instanceof ApiError ? err.message : 'Could not update modes.'),
      },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Connection modes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <section>
          <h3 className="mb-1.5 text-sm font-medium">Protocols</h3>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {PROTOCOLS.map((p) => (
              <label key={p} className="flex items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  checked={protocols.includes(p)}
                  onChange={() => setProtocols((s) => toggle(s, p))}
                />
                {p}
              </label>
            ))}
            {retired.map((p) => {
              const stillArmed = protocols.includes(p);
              return (
                <label
                  key={p}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground"
                  title={
                    stillArmed
                      ? `${p} is retired — uncheck to remove it. It cannot be re-armed.`
                      : `${p} is retired and can no longer be armed.`
                  }
                >
                  <input
                    type="checkbox"
                    checked={stillArmed}
                    disabled={!stillArmed}
                    onChange={() => setProtocols((s) => toggle(s, p))}
                  />
                  <span className="line-through">{p}</span>
                  <span className="text-xs">(retired)</span>
                </label>
              );
            })}
          </div>
        </section>

        <section>
          <h3 className="mb-1.5 text-sm font-medium">Capabilities</h3>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {CAPABILITIES.map((c) => (
              <label key={c} className="flex items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  checked={capabilities.includes(c)}
                  onChange={() => setCapabilities((s) => toggle(s, c))}
                />
                {c}
              </label>
            ))}
          </div>
        </section>

        <Button onClick={save} disabled={!dirty || update.isPending} size="sm">
          {update.isPending ? 'Saving…' : 'Save modes'}
        </Button>
      </CardContent>
    </Card>
  );
}
